import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion, requestIdleCallback } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';
import { SearchUpdateEvent } from '@shopify/events';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;
  #pendingTerm = null;
  #renderedTerm = null;
  #scrollFrame;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();
    if (this.#controller.signal.aborted) this.#controller = new AbortController();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        if (this.isConnected && !this.refs.searchInput.value.trim()) this.#loadEmptyState();
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#activeFetch?.abort();
    this.#pendingTerm = null;
    this.#renderedTerm = null;
    this.#emptyStateLoaded = false;
    if (this.#scrollFrame !== undefined) cancelAnimationFrame(this.#scrollFrame);
    this.#scrollFrame = undefined;
    this.refs.predictiveSearchResults.removeAttribute('aria-busy');
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (!this.isConnected || this.#emptyStateLoaded || this.refs.searchInput.value.trim()) return;
    this.#emptyStateLoaded = true;
    this.#resetSearch();
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    // Let the input method finish composing before handling Enter or Escape.
    if (event.isComposing || event.keyCode === 229) return;
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (event.target && event.target !== this.refs.searchInput) return;

    if (event.key === 'Enter' && (!this.#allResultsItems?.length || this.#renderedTerm !== this.refs.searchInput.value.trim())) {
      // Old suggestions can remain visible while the next debounced read is pending.
      // Never open their single product or keyboard selection for a NEW query.
      event.preventDefault();
      this.#openFullSearch();
      return;
    }

    // Horizontal arrows fall through to native text-caret movement, which the
    // browser already mirrors in RTL.
    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        this.#isKeyboardNavigation = false;
        this.#allResultsItems.forEach(item => {
          item.classList.remove('keyboard-focus');
          item.removeAttribute('aria-selected');
        });
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          event.preventDefault();
          this.#openFullSearch();
        }
        break;
      }
    }
  };

  #openFullSearch() {
    const term = this.refs.searchInput.value.trim();
    if (!term) return;
    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('type', 'product');
    url.searchParams.set('filter.v.availability', '1');
    url.searchParams.set('options[unavailable]', 'hide');
    window.location.href = url.toString();
  }

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (!this.isConnected) return;
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!this.isConnected || event?.isComposing) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    if (this.#scrollFrame !== undefined) cancelAnimationFrame(this.#scrollFrame);
    this.#scrollFrame = requestAnimationFrame(() => {
      this.#scrollFrame = undefined;
      if (!this.isConnected) return;
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId || !this.isConnected) return;
    if (this.#renderedTerm === searchTerm || (this.#pendingTerm === searchTerm && !this.#activeFetch?.signal.aborted)) return;
    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[options][unavailable]', 'hide');
    const { predictiveSearchResults } = this.refs;
    const controller = this.#createAbortController();
    this.#pendingTerm = searchTerm;
    predictiveSearchResults.setAttribute('aria-busy', 'true');
    const deferred = SearchUpdateEvent.createPromise();
    // Keep the event promise rejectable for consumers without an unhandled orphan rejection.
    deferred.promise.catch(() => {});
    this.dispatchEvent(new SearchUpdateEvent({ search: {query: searchTerm}, promise: deferred.promise }));
    try {
      const markup = await sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
      if (!this.#isCurrent(controller) || this.refs.searchInput.value.trim() !== searchTerm) {
        deferred.reject(new Error('Superseded search response'));
        return;
      }
      if (!markup) throw new Error('Search markup unavailable');
      morph(predictiveSearchResults, markup);
      this.#renderedTerm = searchTerm;
      this.#resetScrollPositions();
      deferred.resolve({totalCount: predictiveSearchResults.querySelectorAll('[ref="resultsItems[]"]').length});
    } catch (error) {
      deferred.reject(error);
      if (this.#isCurrent(controller) && this.refs.searchInput.value.trim() === searchTerm) {
        this.#showNotice('Не вдалося оновити підказки. Натисни Enter або «Переглянути всі», щоб відкрити повний пошук.');
      }
    } finally {
      if (this.#activeFetch === controller) {
        this.#pendingTerm = null;
        predictiveSearchResults.removeAttribute('aria-busy');
      }
    }
  }

  #isCurrent(controller) {
    return this.isConnected && this.#activeFetch === controller && !controller.signal.aborted;
  }

  #showNotice(text) {
    const message = document.createElement('p');
    message.setAttribute('role', 'status');
    message.textContent = text;
    message.style.padding = '1rem';
    this.refs.predictiveSearchResults.replaceChildren(message);
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('type', 'product');
    url.searchParams.set('filter.v.availability', '1');
    url.searchParams.set('options[unavailable]', 'hide');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    if (!this.isConnected) return;
    const { predictiveSearchResults, searchInput } = this.refs;
    this.#currentIndex = -1;
    searchInput.value = '';
    this.#pendingTerm = null;
    this.#renderedTerm = null;
    this.#hideResetButton();
    const controller = this.#createAbortController();
    predictiveSearchResults.setAttribute('aria-busy', 'true');
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('page');
      const markup = await sectionRenderer.getSectionHTML('predictive-search-empty', false, url);
      if (!this.#isCurrent(controller) || searchInput.value.trim()) return;
      if (!markup) throw new Error('Empty search markup unavailable');
      const empty = new DOMParser().parseFromString(markup, 'text/html').querySelector('.predictive-search-empty-section');
      if (!empty) throw new Error('Empty search section unavailable');
      if (RecentlyViewed.getProducts().length) {
        let viewed = null;
        try { viewed = await this.#getRecentlyViewedProductsMarkup(); } catch {}
        if (!this.#isCurrent(controller) || searchInput.value.trim()) return;
        if (viewed) {
          const products = new DOMParser().parseFromString(viewed, 'text/html').getElementById('predictive-search-products');
          const destination = empty.querySelector('#predictive-search-products');
          if (products && destination) {
            for (const child of products.children) if (child instanceof HTMLElement) child.setAttribute('ref', 'recentlyViewedWrapper');
            destination.prepend(...products.children);
          }
        }
      }
      if (!this.#isCurrent(controller) || searchInput.value.trim()) return;
      morph(predictiveSearchResults, empty);
      this.#emptyStateLoaded = true;
      this.#resetScrollPositions();
    } catch {
      if (this.#isCurrent(controller) && !searchInput.value.trim()) {
        this.#emptyStateLoaded = false;
        this.#showNotice('Введи назву товару. Повний пошук доступний через Enter або кнопку «Переглянути всі».');
      }
    } finally {
      if (this.#activeFetch === controller) predictiveSearchResults.removeAttribute('aria-busy');
    }
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
